/**
 * Auth API client - handles HTTP calls to the auth endpoints.
 * Separated from React components/hooks to comply with react-refresh rules.
 */

import type { AuthUser } from '@provendoro/shared/types'
import type { RegisterInput, LoginInput, ForgotPasswordInput, ResetPasswordInput } from '@provendoro/shared/validation'

interface AuthApiResponse<T> {
  data?: T
  error?: { message: string; code: string }
}

async function authFetch<T>(
  url: string,
  options?: RequestInit,
): Promise<AuthApiResponse<T>> {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })

  return response.json() as Promise<AuthApiResponse<T>>
}

/** Register with email/password */
export async function signUp(input: RegisterInput): Promise<AuthApiResponse<{ user: AuthUser }>> {
  return authFetch<{ user: AuthUser }>('/api/auth/sign-up/email', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/** Login with email/password */
export async function signIn(input: LoginInput): Promise<AuthApiResponse<{ user: AuthUser }>> {
  return authFetch<{ user: AuthUser }>('/api/auth/sign-in/email', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/** Logout */
export async function signOut(): Promise<AuthApiResponse<{ success: boolean }>> {
  return authFetch<{ success: boolean }>('/api/auth/sign-out', {
    method: 'POST',
  })
}

/** Get current session */
export async function getSessionUser(): Promise<AuthApiResponse<{ user: AuthUser | null; expiresAt?: number }>> {
  return authFetch<{ user: AuthUser | null; expiresAt?: number }>('/api/auth/session')
}

/** Request password reset */
export async function forgotPassword(input: ForgotPasswordInput): Promise<AuthApiResponse<{ success: boolean }>> {
  return authFetch<{ success: boolean }>('/api/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/** Reset password with token */
export async function resetPassword(input: ResetPasswordInput): Promise<AuthApiResponse<{ success: boolean }>> {
  return authFetch<{ success: boolean }>('/api/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/** Get Google OAuth redirect URL */
export function getGoogleAuthUrl(): string {
  return '/api/auth/google'
}
